import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  type SignInInput,
} from 'aws-amplify/auth';
import { setAuthToken, clearAuthToken } from '../api';

interface User {
  username: string;
  email?: string;
  // TODO: Add admin role/permissions when admin Cognito groups are set up
  isAdmin?: boolean;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
  });

  // Check for existing session on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        setAuthToken(idToken);
      }

      // TODO: Check for admin group membership when admin Cognito groups are set up
      // const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined;
      // const isAdmin = groups?.includes('admins') ?? false;

      setState({
        user: {
          username: currentUser.username,
          email: currentUser.signInDetails?.loginId,
          isAdmin: true, // Temporary: treat all authenticated users as admins
        },
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch {
      // No authenticated user
      clearAuthToken();
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const signInInput: SignInInput = {
        username: email,
        password,
      };

      const result = await signIn(signInInput);

      if (result.isSignedIn) {
        // Get the token after successful sign in
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (idToken) {
          setAuthToken(idToken);
        }

        const currentUser = await getCurrentUser();

        // TODO: Verify admin group membership
        // const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined;
        // if (!groups?.includes('admins')) {
        //   await signOut();
        //   throw new Error('Access denied. Admin privileges required.');
        // }

        setState({
          user: {
            username: currentUser.username,
            email: currentUser.signInDetails?.loginId,
            isAdmin: true,
          },
          isLoading: false,
          isAuthenticated: true,
          error: null,
        });
      } else {
        // Handle next steps (e.g., MFA, new password required)
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Additional authentication step required',
        }));
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Login failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      clearAuthToken();
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString() ?? null;

      if (idToken) {
        setAuthToken(idToken);
      }

      return idToken;
    } catch {
      clearAuthToken();
      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        user: null,
      }));
      return null;
    }
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
